import { PlayCircle } from "@mui/icons-material"
import { Box, Button, CircularProgress, Grid2 as Grid, Paper, TextField } from "@mui/material"
import Stack from "@mui/material/Stack"
import { DataGrid, GridColDef, GridRowsProp } from "@mui/x-data-grid"
import { useMeasure } from "@uidotdev/usehooks"
import useModalTrigger from "magik-react-hooks/useModalTrigger"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import CustomToolbar from "../components/CustomToolbar"
import DialogConfirm from "../components/DialogConfirm"
import DialogForm from "../components/DialogForm"
import IconButtonDelete from "../components/IconButtonDelete"
import Layout from "../components/Layout"
import { PageTitle } from "../components/PageTitle"
import Paginator from "../components/Paginator"
import DocumentoForm from "../components/forms/DocumentoForm"
// import { SelectFormato, SelectVocabolario } from "../components/forms/formsHelpers"
import { useDocumentiList, useDocumentoCreate, useDocumentoDelete, useDownloadDocumenti } from "../hooks/documenti"
import { useQsFilters } from "../hooks/filters"
import useUserCanWriteAndDelete from "../hooks/permissions"
import { useSnackbarMessage } from "../hooks/snackbar"
import { Documento, Supporto, Vocabolario } from "../types"
import LangLink from "../components/LangLink"
import { useIsMobile } from "../hooks/mobile"
import filesaver from "file-saver"
import { useIsTablet } from "../hooks/tablet"
import { ACVocabolarioMulti } from "../components/forms/autocompletes"
import { useAuth } from "../auth"
import { PAGE_SIZE_OPTIONS, usePageSize } from "../hooks/pageSize"

const initFilters = (params: URLSearchParams) => ({
  page: Number(params.get("page") ?? 1),
  search: params.get("search") ?? "",
  ordering: params.get("ordering") ?? "titolo_proprio",
  formato: params.get("formato") ?? "",
  elemento: params.get("elemento") ?? "",
  genere: (params.getAll("genere") as unknown[]) ?? [],
  cronologia_da: params.get("cronologia_da") ?? "",
  cronologia_a: params.get("cronologia_a") ?? "",
})

type DocumentiProps = {
  fondoId?: number
  supportoId?: number
}

export default function Documenti({ fondoId, supportoId }: DocumentiProps) {
  const pageSize = usePageSize()
  const { filters, setFilters, uiFilters, setFiltersDebounced } = useQsFilters(initFilters)
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { user } = useAuth()
  const isMobile = useIsMobile() || user?.display === "mobile"
  const isTablet = (useIsTablet() || user?.display === "tablet") && !isMobile

  const canWrite = useUserCanWriteAndDelete()

  const columns: GridColDef[] = useMemo(() => {
    let cols: GridColDef[] = []
    cols.push({
      field: "titolo_attribuito",
      headerName: t("documento.titolo_attribuito"),
      flex: 1,
      renderCell: (params) => (
        <LangLink to={`/documenti/${params.row.id}`} style={{ color: "black" }}>
          {params.value}
        </LangLink>
      ),
    })
    if (isMobile) {
      return cols
    }

    if (isTablet) {
      cols.push({
        field: "titolo_proprio",
        headerName: t("documento.titolo_proprio"),
        flex: 1,
      })

      if (canWrite) {
        cols.push({
          field: "id",
          headerName: t("general.azioni"),
          flex: 1,
          display: "flex",
          align: "right",
          headerAlign: "right",
          hideSortIcons: true,
          sortable: false,
          renderCell: (params) => (
            <IconButtonDelete disabled={!params.row.can_delete} onClick={() => deleteModalActions.open(params.row)} />
          ),
        })
      }

      return cols
    }

    cols.push(
      {
        field: "titolo_proprio",
        headerName: t("documento.titolo_proprio"),
        flex: 1,
        renderCell: (params) => (
          <LangLink to={`/documenti/${params.row.id}`} style={{ color: "black" }}>
            {params.value || t("general.senza_titolo")}
          </LangLink>
        ),
      },

      {
        field: "supporti_data",
        headerName: t("general.supporti"),
        flex: 1,
        renderCell: (params) => params.value.map((s: Supporto) => s.segnatura).join(", "),
      },
      {
        field: "cronologia_da",
        headerName: t("documento.cronologia_da"),
        flex: 1,
      },
      {
        field: "cronologia_a",
        headerName: t("documento.cronologia_a"),
        flex: 1,
      },
      {
        field: "genere_data",
        headerName: t("documento.genere"),
        sortable: false,
        flex: 1,
        renderCell: (params) => {
          return <span>{params.row?.genere_data?.map((genere: Vocabolario) => genere.termine).join(", ")}</span>
        },
      },
    )

    if (canWrite) {
      cols.push({
        field: "id",
        headerName: t("general.azioni"),
        flex: 1,
        display: "flex",
        align: "right",
        headerAlign: "right",
        hideSortIcons: true,
        sortable: false,
        renderCell: (params) => (
          <IconButtonDelete disabled={!params.row.can_delete} onClick={() => deleteModalActions.open(params.row)} />
        ),
      })
    }

    return cols
  }, [t, canWrite, isMobile, isTablet])

  const { data, isLoading } = useDocumentiList({
    page: String(filters.page),
    page_size: String(pageSize),
    search: filters.search,
    ordering: filters.ordering,
    formato: filters.formato,
    elemento: filters.elemento,
    fondo: fondoId ? String(fondoId) : "",
    supporto: supportoId ? String(supportoId) : "",
    genere: filters.genere as string[],
    cronologia_da: filters.cronologia_da,
    cronologia_a: filters.cronologia_a,
  })

  const { mutateAsync: create } = useDocumentoCreate()
  const { mutateAsync: remove } = useDocumentoDelete()
  const { mutateAsync: downloadDocumenti, isPending } = useDownloadDocumenti({
    search: filters.search,
    ordering: filters.ordering,
    formato: filters.formato,
    elemento: filters.elemento,
    fondo: fondoId ? String(fondoId) : "",
    supporto: supportoId ? String(supportoId) : "",
    genere: filters.genere as string[],
    cronologia_da: filters.cronologia_da,
    cronologia_a: filters.cronologia_a,
  })

  const numPages = useMemo(() => {
    if (!data) return 1
    return Math.ceil(data.count / pageSize)
  }, [data, pageSize])

  const [ref, { height }] = useMeasure()
  const [toolbarRef, { height: toolbarHeight }] = useMeasure()
  const tableHeight = useMemo(() => {
    if (!height || !toolbarHeight) return
    return height - toolbarHeight
  }, [height, toolbarHeight])

  const [createModalState, createModalActions] = useModalTrigger()
  const [deleteModalState, deleteModalActions] = useModalTrigger()

  const showMessage = useSnackbarMessage()

  const filtersCount = useMemo(() => {
    let count = 0
    if (filters.formato) count++
    if (filters.elemento) count++
    if (filters.genere && (filters.genere as string[]).length > 0) {
      count++
    }
    if (filters.cronologia_da) count++
    if (filters.cronologia_a) count++

    return count
  }, [filters.formato, filters.elemento, filters.genere, filters.cronologia_da, filters.cronologia_a])

  return (
    <Layout padding>
      <Stack sx={{ height: "100%" }}>
        {!fondoId && !supportoId && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <PageTitle
              title={t("documento.titolo_lista")}
              breadcrumbs={[
                {
                  label: t("documento.titolo_lista"),
                  iconType: PlayCircle,
                },
              ]}
            />
            <Box>
              {canWrite && !isMobile && (
                <Button
                  sx={{
                    marginRight: 1,
                  }}
                  variant="contained"
                  disabled={isPending}
                  color="secondary"
                  onClick={() => {
                    downloadDocumenti().then((blob) => {
                      filesaver.saveAs(blob, `documenti_${new Date().toISOString().split("T")[0]}.xlsx`)
                    })
                  }}
                >
                  {isPending ? <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} /> : null}
                  {t("documento.download_excel")}
                </Button>
              )}
              {canWrite && !isMobile && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    createModalActions.open()
                  }}
                >
                  {t("documento.cta_aggiungi")}
                </Button>
              )}
            </Box>
          </Box>
        )}
        <Box flexGrow={1}>
          <Paper sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
            <CustomToolbar
              count={data?.count}
              search={uiFilters.search}
              setSearch={(search) => setFiltersDebounced({ ...filters, search, page: 1 })}
              clearFilters={() =>
                setFilters({
                  search: "",
                  page: 1,
                  formato: "",
                  elemento: "",
                  genere: [],
                  cronologia_da: "",
                  cronologia_a: "",
                })
              }
              filtersCount={filtersCount}
              ref={toolbarRef}
            >
              <Grid container spacing={2}>
                <Grid size={3}>
                  <ACVocabolarioMulti
                    label={t("fondo.genere")}
                    vocabolario="fondo_genere"
                    margin="dense"
                    variant="outlined"
                    fullWidth
                    value={uiFilters.genere as number[]}
                    onChange={(value) => {
                      setFilters({ ...filters, genere: value, page: 1 })
                    }}
                  />
                </Grid>
                <Grid size={3}>
                  <TextField
                    label={t("fondo.cronologia_da")}
                    type="number"
                    margin="dense"
                    size="small"
                    variant="outlined"
                    fullWidth
                    value={uiFilters.cronologia_da}
                    onChange={(e) => setFilters({ ...filters, cronologia_da: e.target.value, page: 1 })}
                  />
                </Grid>
                <Grid size={3}>
                  <TextField
                    label={t("fondo.cronologia_a")}
                    type="number"
                    size="small"
                    margin="dense"
                    variant="outlined"
                    fullWidth
                    value={uiFilters.cronologia_a}
                    onChange={(e) => setFilters({ ...filters, cronologia_a: e.target.value, page: 1 })}
                  />
                </Grid>
              </Grid>
            </CustomToolbar>
            <div
              style={{
                flexGrow: 1,
                overflow: "scroll",
                height: tableHeight ?? undefined,
              }}
              ref={ref}
            >
              {height && (
                <DataGrid
                  getRowId={(row) => row.id}
                  disableColumnMenu
                  disableColumnResize
                  // density="compact"
                  pageSizeOptions={PAGE_SIZE_OPTIONS}
                  paginationModel={{ page: 0, pageSize }}
                  sx={{
                    padding: 2,
                    "&.MuiDataGrid-root": {
                      border: "none",
                    },
                  }}
                  localeText={{
                    noRowsLabel: t("general.nessun_dato_disponibile"),
                  }}
                  loading={isLoading}
                  slots={{
                    footer: () => (
                      <Paginator
                        numPages={numPages}
                        page={filters.page}
                        setPage={(page) =>
                          setFilters({
                            ...filters,
                            page,
                          })
                        }
                      />
                    ),
                    toolbar: null,
                  }}
                  rows={data?.results ?? ([] as GridRowsProp)}
                  columns={columns}
                  onSortModelChange={(model) => {
                    if (model.length > 0) {
                      const sortSign = model[0].sort === "asc" ? "" : "-"
                      const newOrdering = `${sortSign}${model[0].field}`
                      setFilters({ ordering: newOrdering, page: 1 })
                    }
                  }}
                />
              )}
            </div>
          </Paper>
        </Box>
      </Stack>

      <DialogForm<Documento>
        open={createModalState.isOpen}
        handleClose={createModalActions.close}
        title={t("supporto.cta_aggiungi")}
        FormComponent={DocumentoForm}
        save={(values: Documento) => {
          return create(values).then((response) => {
            createModalActions.close()
            showMessage(t("documento.esito_crea_ok"))
            navigate(`/${i18n.language}/documenti/${response.id}`)
          })
        }}
      />
      <DialogConfirm
        open={deleteModalState.isOpen}
        handleClose={deleteModalActions.close}
        title={t("documento.cta_elimina")}
        subtitle={
          <span>
            {t("documento.cta_subtitle_elimina")}{" "}
            <b>"{deleteModalState.value?.titolo_proprio ?? deleteModalState.value?.titolo_attribuito}"</b>&nbsp;?
          </span>
        }
        textConfirm={t("general.elimina")}
        onConfirm={() => {
          return remove(deleteModalState.value.id)
            .then(() => {
              deleteModalActions.close()
              showMessage(t("documento.esito_elimina_ok"))
            })
            .catch(() => {
              deleteModalActions.close()
              showMessage(t("general.esito_elimina_errore"), "error")
            })
        }}
      />
    </Layout>
  )
}
